import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { dispatcherService } from '../../services/dispatcherService';
import { orderService } from '../../services/orderService';
import DispatcherStatusBadge from '../../components/dispatchers/DispatcherStatusBadge';
import DataTable from '../../components/common/DataTable';
import OrderStatusBadge from '../../components/orders/OrderStatusBadge';
import {
    ChevronLeft,
    User,
    Mail,
    Phone,
    Calendar,
    Clock,
    Edit,
    MessageSquare,
    AlertTriangle
} from 'lucide-react';

const DispatcherDetailPage = () => {
    const { dispatcherId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [isLoading, setIsLoading] = useState(true);
    const [dispatcher, setDispatcher] = useState(null);
    const [recentOrders, setRecentOrders] = useState([]);
    const [orderStatsLoading, setOrderStatsLoading] = useState(true);
    const [orderStats, setOrderStats] = useState({
        totalToday: 0,
        completedToday: 0,
        cancelledToday: 0,
        assignmentRatio: 0
    });

    useEffect(() => {
        fetchDispatcherDetails();
    }, [dispatcherId]);

    const fetchDispatcherDetails = async () => {
        setIsLoading(true);
        try {
            const dispatcherData = await dispatcherService.getDispatcherById(dispatcherId);
            setDispatcher(dispatcherData);

            // 獲取該調度員最近處理的訂單
            const ordersData = await orderService.getOrders({
                dispatcher_id: dispatcherId,
                limit: 5,
                sort: 'created_at,desc'
            });
            setRecentOrders(ordersData.data);

            // 獲取調度員的訂單統計數據
            await fetchOrderStats(dispatcherId);
        } catch (error) {
            console.error('Error fetching dispatcher details:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchOrderStats = async (dispatcherId) => {
        setOrderStatsLoading(true);
        try {
            const stats = await dispatcherService.getDispatcherStats(dispatcherId);
            setOrderStats(stats);
        } catch (error) {
            console.error('Error fetching order stats:', error);
        } finally {
            setOrderStatsLoading(false);
        }
    };

    const handleEditDispatcher = () => {
        navigate(`/dispatchers/${dispatcherId}/edit`);
    };

    const handleSendMessage = () => {
        navigate(`/messages/new?recipient_id=${dispatcher?.user_id}&recipient_type=dispatcher`);
    };

    const recentOrdersColumns = [
        {
            header: '訂單編號',
            accessor: 'order_number',
            cell: (row) => (
                <span className="font-medium text-blue-600 cursor-pointer"
                    onClick={() => navigate(`/orders/${row.order_id}`)}
                >
                    {row.order_number}
                </span>
            )
        },
        {
            header: '乘客',
            accessor: 'passenger_name',
            cell: (row) => (
                <span>{row.passenger_name}</span>
            )
        },
        {
            header: '車資',
            accessor: 'fare',
            cell: (row) => (
                <span className="font-medium">${row.actual_fare || row.estimated_fare}</span>
            )
        },
        {
            header: '狀態',
            accessor: 'status',
            cell: (row) => (
                <OrderStatusBadge status={row.status} />
            )
        },
        {
            header: '派單時間',
            accessor: 'assigned_at',
            cell: (row) => (
                <span className="text-sm text-gray-600">
                    {new Date(row.assigned_at || row.created_at).toLocaleString('zh-TW', {
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit'
                    })}
                </span>
            )
        }
    ];

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    if (!dispatcher) {
        return (
            <div className="text-center py-12">
                <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
                <h2 className="text-xl font-bold text-gray-800 mb-2">找不到調度員</h2>
                <p className="text-gray-600 mb-4">無法找到ID為 {dispatcherId} 的調度員資訊。</p>
                <button
                    onClick={() => navigate('/dispatchers')}
                    className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                >
                    返回調度員列表
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center mb-6">
                <button
                    onClick={() => navigate('/dispatchers')}
                    className="p-2 mr-2 rounded-md hover:bg-gray-100"
                >
                    <ChevronLeft className="w-5 h-5" />
                </button>
                <h1 className="text-2xl font-bold">調度員詳情</h1>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* 調度員基本資訊卡片 */}
                <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-100">
                        <div className="flex justify-between items-center">
                            <h3 className="font-bold">基本資訊</h3>
                            <DispatcherStatusBadge status={dispatcher.status} />
                        </div>
                    </div>

                    <div className="p-6">
                        <div className="flex flex-col items-center mb-6">
                            <div className="w-24 h-24 bg-gray-200 rounded-full mb-4 overflow-hidden">
                                {dispatcher.avatar_url ? (
                                    <img
                                        src={dispatcher.avatar_url}
                                        alt={dispatcher.full_name}
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center bg-blue-100 text-blue-500">
                                        <User className="w-12 h-12" />
                                    </div>
                                )}
                            </div>
                            <h4 className="text-xl font-bold">{dispatcher.full_name}</h4>
                            <p className="text-gray-600">
                                {dispatcher.fleet_id ? dispatcher.fleet_name : '全局調度員'}
                            </p>
                        </div>

                        <div className="space-y-4">
                            <div className="flex items-center">
                                <Mail className="w-5 h-5 text-gray-400 mr-3" />
                                <div>
                                    <p className="text-sm text-gray-500">電子郵件</p>
                                    <p>{dispatcher.email}</p>
                                </div>
                            </div>

                            <div className="flex items-center">
                                <Phone className="w-5 h-5 text-gray-400 mr-3" />
                                <div>
                                    <p className="text-sm text-gray-500">聯絡電話</p>
                                    <p>{dispatcher.phone}</p>
                                </div>
                            </div>

                            <div className="flex items-center">
                                <Clock className="w-5 h-5 text-gray-400 mr-3" />
                                <div>
                                    <p className="text-sm text-gray-500">工作時段</p>
                                    <p>{dispatcher.work_shift || '標準工作時段 (09:00-18:00)'}</p>
                                </div>
                            </div>

                            <div className="flex items-center">
                                <Calendar className="w-5 h-5 text-gray-400 mr-3" />
                                <div>
                                    <p className="text-sm text-gray-500">加入時間</p>
                                    <p>{new Date(dispatcher.created_at).toLocaleDateString('zh-TW')}</p>
                                </div>
                            </div>
                        </div>

                        <div className="mt-6 space-y-3">
                            <button
                                onClick={handleEditDispatcher}
                                className="w-full bg-blue-500 text-white py-2 rounded-md font-medium flex items-center justify-center"
                            >
                                <Edit className="w-4 h-4 mr-2" />
                                編輯調度員
                            </button>

                            <button
                                onClick={handleSendMessage}
                                className="w-full border border-gray-300 py-2 rounded-md font-medium flex items-center justify-center"
                            >
                                <MessageSquare className="w-4 h-4 mr-2" />
                                傳送訊息
                            </button>
                        </div>
                    </div>
                </div>

                {/* 統計數據與最近訂單 */}
                <div className="lg:col-span-2 space-y-6">
                    {/* 統計卡片 */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-white p-4 rounded-lg shadow-sm">
                            <p className="text-sm text-gray-500 mb-1">今日總訂單</p>
                            <p className="text-2xl font-bold">
                                {orderStatsLoading ? '-' : orderStats.totalToday}
                            </p>
                        </div>

                        <div className="bg-white p-4 rounded-lg shadow-sm">
                            <p className="text-sm text-gray-500 mb-1">完成訂單</p>
                            <p className="text-2xl font-bold text-green-600">
                                {orderStatsLoading ? '-' : orderStats.completedToday}
                            </p>
                        </div>

                        <div className="bg-white p-4 rounded-lg shadow-sm">
                            <p className="text-sm text-gray-500 mb-1">取消訂單</p>
                            <p className="text-2xl font-bold text-red-600">
                                {orderStatsLoading ? '-' : orderStats.cancelledToday}
                            </p>
                        </div>

                        <div className="bg-white p-4 rounded-lg shadow-sm">
                            <p className="text-sm text-gray-500 mb-1">成功派單率</p>
                            <p className="text-2xl font-bold text-blue-600">
                                {orderStatsLoading ? '-' : orderStats.assignmentRatio}%
                            </p>
                        </div>
                    </div>

                    {/* 最近處理的訂單 */}
                    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-100">
                            <div className="flex justify-between items-center">
                                <h3 className="font-bold">最近處理的訂單</h3>
                                <button
                                    onClick={() => navigate(`/orders?dispatcher_id=${dispatcherId}`)}
                                    className="text-blue-500 text-sm hover:underline"
                                >
                                    查看全部
                                </button>
                            </div>
                        </div>

                        <div>
                            <DataTable
                                columns={recentOrdersColumns}
                                data={recentOrders}
                                emptyMessage="該調度員尚未處理任何訂單"
                            />
                        </div>
                    </div>

                    {/* 權限資訊 */}
                    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-100">
                            <h3 className="font-bold">權限資訊</h3>
                        </div>

                        <div className="p-6">
                            <div className="space-y-4">
                                <div>
                                    <h4 className="font-medium mb-2">角色權限</h4>
                                    <div className="flex flex-wrap gap-2">
                                        <span className="px-3 py-1 bg-blue-100 text-blue-600 rounded-full text-sm">
                                            訂單管理
                                        </span>
                                        <span className="px-3 py-1 bg-blue-100 text-blue-600 rounded-full text-sm">
                                            司機派遣
                                        </span>
                                        <span className="px-3 py-1 bg-blue-100 text-blue-600 rounded-full text-sm">
                                            客戶溝通
                                        </span>
                                        {dispatcher.is_global_admin && (
                                            <span className="px-3 py-1 bg-purple-100 text-purple-600 rounded-full text-sm">
                                                跨車隊派單
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <div>
                                    <h4 className="font-medium mb-2">特殊權限</h4>
                                    <div className="space-y-2">
                                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                                            <span>可以修改車資</span>
                                            <span className={`px-2 py-1 rounded-full text-xs ${dispatcher.can_modify_fare ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-600'
                                                }`}>
                                                {dispatcher.can_modify_fare ? '允許' : '不允許'}
                                            </span>
                                        </div>

                                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                                            <span>可以取消訂單</span>
                                            <span className={`px-2 py-1 rounded-full text-xs ${dispatcher.can_cancel_orders ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-600'
                                                }`}>
                                                {dispatcher.can_cancel_orders ? '允許' : '不允許'}
                                            </span>
                                        </div>

                                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                                            <span>可以延長等候時間</span>
                                            <span className={`px-2 py-1 rounded-full text-xs ${dispatcher.can_extend_waiting_time ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-600'
                                                }`}>
                                                {dispatcher.can_extend_waiting_time ? '允許' : '不允許'}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <h4 className="font-medium mb-2">服務範圍</h4>
                                    <p className="text-gray-600">
                                        {dispatcher.fleet_id
                                            ? `限定於 ${dispatcher.fleet_name} 車隊範圍內`
                                            : '全平台範圍內的調度服務'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DispatcherDetailPage;